import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Upload, Image as ImageIcon, Trash2, LogOut, Plus, Settings, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { galleryAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const AdminUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('portraits');
  const [isUploading, setIsUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [categories, setCategories] = useState([]);
  const { isAuthenticated, user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const fetchCategories = async () => {
    try {
      const response = await galleryAPI.getCategories();
      setCategories(response);
      if (response.length > 0) {
        setCategory(response[0].slug);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchImages = async () => {
    try {
      const allImages = await galleryAPI.getImages('all');
      setImages(allImages);
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if not authenticated (after all hooks/declarations to avoid TDZ issues)
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wählen Sie eine JPG, PNG oder WEBP Datei",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "Datei zu groß",
        description: "Maximale Dateigröße ist 50MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast({
        title: "Fehler",
        description: "Bitte wählen Sie ein Bild aus",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('title', title);
      formData.append('category', category);

      await galleryAPI.uploadImage(formData);
      
      toast({
        title: "Erfolg!",
        description: "Bild wurde erfolgreich hochgeladen und mit Wasserzeichen versehen",
      });

      // Reset form
      setSelectedFile(null);
      setPreviewUrl(null);
      setTitle('');
      setCategory('portraits');
      
      // Refresh images
      fetchImages();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Fehler",
        description: error.response?.data?.detail || "Bild konnte nicht hochgeladen werden",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Möchten Sie dieses Bild wirklich löschen?')) {
      return;
    }

    try {
      await galleryAPI.deleteImage(imageId);
      toast({
        title: "Gelöscht",
        description: "Bild wurde erfolgreich gelöscht",
      });
      fetchImages();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Fehler",
        description: "Bild konnte nicht gelöscht werden",
        variant: "destructive"
      });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-heading font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">Eingeloggt als: {user?.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/account')}
                data-testid="admin-account-nav-btn"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <KeyRound size={18} />
                Konto
              </button>
              <button
                onClick={() => navigate('/admin/settings')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Settings size={18} />
                Einstellungen
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut size={18} />
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-heading font-semibold mb-6 flex items-center gap-2">
            <Plus size={24} />
            Neues Bild hochladen
          </h2>
          <form onSubmit={handleUpload} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bildtitel
                </label>
                <input
                  type="text"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                  placeholder="z.B. Business Portrait"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kategorie
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                >
                  {categories.map((cat) => (
                    <option key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bild auswählen
              </label>
              <div className="mt-1">
                {previewUrl ? (
                  <div className="relative mb-4">
                    <img src={previewUrl} alt="Preview" className="mx-auto h-64 object-cover rounded-lg border-2 border-gray-300" />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                      className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full hover:bg-red-700"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ) : null}
                
                <label 
                  className={`flex flex-col items-center justify-center w-full h-40 px-6 pt-5 pb-6 border-2 border-dashed rounded-lg cursor-pointer transition-all ${
                    isDragging 
                      ? 'border-primary bg-blue-50 border-solid' 
                      : 'border-gray-300 hover:border-primary hover:bg-gray-50'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-2 text-center">
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <span className="font-medium text-primary hover:text-gray-800">
                        {isDragging ? 'Datei hier ablegen...' : 'Klicken Sie hier um eine Datei auszuwählen'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      oder ziehen Sie eine Datei hierher
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, WEBP bis zu 50MB
                    </p>
                    {selectedFile && (
                      <p className="text-sm font-medium text-green-600">
                        ✓ {selectedFile.name}
                      </p>
                    )}
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFileSelect}
                  />
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={isUploading || !selectedFile}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-primary text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Upload size={20} />
              {isUploading ? 'Wird hochgeladen...' : 'Mit Wasserzeichen hochladen'}
            </button>
          </form>
        </div>

        {/* Image Gallery */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-heading font-semibold mb-6">Hochgeladene Bilder ({images.length})</h2>
          {loading ? (
            <p className="text-center text-gray-500 py-8">Wird geladen...</p>
          ) : images.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Keine Bilder vorhanden</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => (
                <div key={image.id} className="relative group">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${image.thumbnail}`}
                    alt={image.title}
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all rounded-lg flex items-center justify-center">
                    <button
                      onClick={() => handleDelete(image.id)}
                      className="opacity-0 group-hover:opacity-100 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-all"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{image.title}</p>
                    <p className="text-xs text-gray-500 uppercase">{image.category}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUpload;
